import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { environment } from './environments/environment';

/*
// Set the base href dynamically
function setBaseHref() {
	const baseElement = document.querySelector('base');
	if (!baseElement) {
		console.error('The <base> element is missing in the DOM.');
		return;
	}

	const path = window.location.pathname;

	// Extract the first segment of the URL path
	const segments = path.split('/').filter(Boolean);
	const firstSegment = segments[0];

	// Known subdirectories from environment
	const knownSubdirs: any[] = environment?.RC_LIST || [];

	if (firstSegment && knownSubdirs.includes(firstSegment)) {
		// We're in a known subdirectory
		baseElement.href = '/' + firstSegment + '/';
		// Store the current subdirectory globally for API calls
		(window as any).currentSubdir = firstSegment;
	} else {
		// We're at the root
		baseElement.href = '/';
		(window as any).currentSubdir = '';
	}

	console.log('Base href set to:', baseElement.href);
	console.log('Current subdirectory:', (window as any).currentSubdir);
}

// Set the base href before bootstrapping the app
setBaseHref();
 */
// main.ts or a script in index.html
(function () {
	// Skip RC logic for localhost or 127.0.0.1
	if (
		window.location.hostname.includes('localhost') ||
		window.location.hostname.includes('127.0.0.1') ||
		window.location.hostname === 'rms.katdev.com'
	) {
		// console.log('Running on localhost, skipping RC logic.');
		return;
	}

	// Check if the URL is the main domain without an RC
	const path = window.location.pathname;
	const urlParts = path.split('/').filter((part) => part);
	const firstPart = urlParts[0];

	// Replace `environment.RC_LIST` with your actual list of valid RCs
	const validRCs = environment.RC_LIST; // Example list of valid RCs

	if (path === '/' || path === '') {
		// Clear localStorage and sessionStorage for a fresh start
		localStorage.clear();
		sessionStorage.clear();
		console.log('Accessed main domain without RC. Cleared storage.');

		// Redirect to the RC selection page
		window.location.href = '/select-rc';
	} else if (firstPart && validRCs.includes(firstPart)) {
		// If the URL starts with a valid RC, ensure it's stored in sessionStorage
		sessionStorage.setItem('currentRc', firstPart);
		console.log(
			`Valid RC detected: ${firstPart}. Stored in sessionStorage.`
		);

		// Check if the path is just the RC (e.g., /kiosk1)
		if (urlParts.length === 1) {
			// Don't redirect, let Angular handle this route
			console.log(
				'URL is just the RC. Letting Angular router handle it.'
			);
		}
	} else {
		// If no valid RC is found, check sessionStorage for a stored RC
		const currentRc = sessionStorage.getItem('currentRc');
		if (currentRc && !path.startsWith(`/${currentRc}`)) {
			// Redirect to the URL with the stored RC prepended
			window.location.href = `/${currentRc}${path}`;
			console.log(
				`No valid RC in URL. Prepending stored RC: ${currentRc}`
			);
		}
	}
})();

// Custom console styling for different environments
function displayEnvironmentInfo() {
	const appName = 'RMS Angular Application';
	const version = '1.0.0'; // You can get this from package.json if needed

	if (environment.production) {
		// Production - Clean and professional
		console.log(
			`%c🚀 ${appName} %cv${version}%c - Production Mode`,
			'color: #10B981; font-weight: bold; font-size: 14px;',
			'color: #6B7280; font-size: 12px;',
			'color: #EF4444; font-weight: bold; font-size: 14px;'
		);
	} else {
		// Detect current mode based on URL or environment
		const hostname = window.location.hostname;
		let currentMode = 'development';

		if (hostname.includes('staging')) {
			currentMode = 'staging';
		} else if (hostname.includes('beta')) {
			currentMode = 'beta';
		} else if (hostname.includes('uat')) {
			currentMode = 'uat';
		} else if (hostname.includes('dev')) {
			currentMode = 'development';
		}

		// Development modes with different styling
		const modeStyles = {
			development: {
				emoji: '🔧',
				color: '#3B82F6', // Blue
				bgColor: '#EFF6FF',
				textColor: '#1E40AF'
			},
			staging: {
				emoji: '🧪',
				color: '#F59E0B', // Amber
				bgColor: '#FFFBEB',
				textColor: '#D97706'
			},
			beta: {
				emoji: '🎯',
				color: '#8B5CF6', // Purple
				bgColor: '#F5F3FF',
				textColor: '#7C3AED'
			},
			uat: {
				emoji: '🧭',
				color: '#06B6D4', // Cyan
				bgColor: '#ECFEFF',
				textColor: '#0891B2'
			}
		};

		const style =
			modeStyles[currentMode as keyof typeof modeStyles] ||
			modeStyles.development;

		// Main banner with gradient background
		console.log(
			`%c ${style.emoji} ${appName} %c v${version} %c ${currentMode.toUpperCase()} MODE `,
			`background: linear-gradient(90deg, ${style.color}, ${style.textColor}); color: white; padding: 8px 12px; border-radius: 4px 0 0 4px; font-weight: bold; font-size: 13px;`,
			`background: #374151; color: white; padding: 8px 8px; font-size: 11px;`,
			`background: ${style.textColor}; color: white; padding: 8px 12px; border-radius: 0 4px 4px 0; font-weight: bold; font-size: 13px;`
		);

		// Environment details
		console.groupCollapsed(
			`%c📋 Environment Configuration`,
			`color: ${style.color}; font-weight: bold;`
		);
		console.log(
			`%cAPI URL: %c${environment.API_URL}`,
			'color: #6B7280; font-weight: bold;',
			`color: ${style.textColor};`
		);
		console.log(
			`%cSignalR URL: %c${environment.Signalr_URL}`,
			'color: #6B7280; font-weight: bold;',
			`color: ${style.textColor};`
		);
		if ((environment as any).INVENTORY_API_URL) {
			console.log(
				`%cInventory API: %c${(environment as any).INVENTORY_API_URL}`,
				'color: #6B7280; font-weight: bold;',
				`color: ${style.textColor};`
			);
		}
		console.log(
			`%cMode: %c${currentMode}`,
			'color: #6B7280; font-weight: bold;',
			`color: ${style.textColor};`
		);
		console.log(
			`%cProduction: %c${environment.production}`,
			'color: #6B7280; font-weight: bold;',
			`color: ${environment.production ? '#EF4444' : '#10B981'};`
		);
		console.groupEnd();

		// Development tips
		if (currentMode === 'development') {
			console.log(
				`%c💡 Development Tips`,
				'color: #F59E0B; font-weight: bold; font-size: 12px; margin-top: 8px;'
			);
			console.log(
				`%c• Use Angular DevTools for debugging\n• Check Network tab for API calls\n• Enable Redux DevTools if using state management`,
				'color: #6B7280; font-size: 11px; line-height: 1.4;'
			);
		}
	}

	// Performance timing (if available)
	if (performance && performance.timing) {
		const loadTime =
			performance.timing.loadEventEnd -
			performance.timing.navigationStart;
		if (loadTime > 0) {
			console.log(
				`%c⚡ Page loaded in ${loadTime}ms`,
				'color: #10B981; font-weight: bold; font-size: 11px;'
			);
		}
	}
}

// Display custom environment info
displayEnvironmentInfo();

bootstrapApplication(AppComponent, appConfig).catch((err: any) =>
	console.error(err)
);
